import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSummary, fetchSyncLogs, triggerSync } from "../api";
import api from "../api";
import { RefreshCw, Calendar } from "lucide-react";

const fetchProductMTD = () => api.get("/profit/by-product-v2").then(r => r.data);
const fetchSummaryRange = (start, end) =>
  api.get("/dashboard/summary-range", { params: { start_date: start, end_date: end } }).then(r => r.data);

const fmt = (n) => new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 0
}).format(Math.abs(n || 0));

const today = new Date().toISOString().split("T")[0];
const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0];

export default function Dashboard() {
  const [mode, setMode] = useState("mtd"); // mtd or range
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [applied, setApplied] = useState({ start: firstOfMonth, end: today });

  const { data: summary, isLoading, refetch } = useQuery({
    queryKey: ["summary"], queryFn: fetchSummary
  });

  const { data: rangeData, isLoading: rangeLoading } = useQuery({
    queryKey: ["summary-range", applied.start, applied.end],
    queryFn: () => fetchSummaryRange(applied.start, applied.end),
    enabled: mode === "range"
  });

  const { data: products } = useQuery({
    queryKey: ["profit-product-mtd"], queryFn: fetchProductMTD
  });

  const handleSync = async (type) => {
    await triggerSync(type, { days: 30 });
    setTimeout(() => refetch(), 3000);
  };

  const s = mode === "mtd" ? (summary || {}) : (rangeData || {});
  const isLoadingData = mode === "mtd" ? isLoading : rangeLoading;

  const periodLabel = mode === "mtd"
    ? summary?.month
    : `${applied.start} to ${applied.end}`;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Overview</div>
          <div className="page-subtitle">{periodLabel}</div>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
          {/* Mode toggle */}
          <div style={{ display: "flex", background: "#f0f0f0", borderRadius: "8px", padding: "3px" }}>
            {[["mtd", "This Month"], ["range", "Custom Range"]].map(([v, l]) => (
              <button key={v} onClick={() => setMode(v)}
                style={{
                  padding: "6px 12px", borderRadius: "6px", border: "none", cursor: "pointer",
                  fontWeight: 600, fontSize: "0.8rem",
                  background: mode === v ? "white" : "transparent",
                  color: mode === v ? "#4361ee" : "#888",
                  boxShadow: mode === v ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
                }}>{l}</button>
            ))}
          </div>

          {/* Date range inputs */}
          {mode === "range" && (
            <>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                style={{ padding: "6px 10px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }} />
              <span style={{ color: "#888" }}>to</span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                style={{ padding: "6px 10px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }} />
              <button className="btn btn-primary btn-sm"
                onClick={() => setApplied({ start: startDate, end: endDate })}>
                <Calendar size={14} /> Apply
              </button>
            </>
          )}

          {mode === "mtd" && (
            <>
              <button className="btn btn-outline btn-sm" onClick={() => handleSync("orders")}><RefreshCw size={14} /> Orders</button>
              <button className="btn btn-outline btn-sm" onClick={() => handleSync("fees")}><RefreshCw size={14} /> Fees</button>
              <button className="btn btn-outline btn-sm" onClick={() => handleSync("financials")}><RefreshCw size={14} /> Ads & More</button>
            </>
          )}
        </div>
      </div>

      {isLoadingData ? <div className="loading">Loading...</div> : (
        <>
          {/* Top summary cards */}
          <div className="cards-grid" style={{ marginBottom: "1.5rem" }}>
            <div className="card blue">
              <div className="card-label">Revenue</div>
              <div className="card-value">{fmt(s.revenue)}</div>
              <div className="card-sub">{s.units_sold} units · {s.orders || ""} orders</div>
            </div>
            <div className={`card ${(s.profit || 0) >= 0 ? "green" : "red"}`}>
              <div className="card-label">Net Profit</div>
              <div className="card-value">{fmt(s.profit)}</div>
              <div className="card-sub">Margin: {s.margin_pct}%</div>
            </div>
            <div className="card">
              <div className="card-label">FBA Stock</div>
              <div className="card-value">{(s.total_fba_stock || summary?.total_fba_stock || 0).toLocaleString()}</div>
              <div className="card-sub">Total units in FBA</div>
            </div>
          </div>

          {/* Cost breakdown */}
          <div className="chart-card" style={{ marginBottom: "1.5rem" }}>
            <div className="chart-title">Cost Breakdown</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "1rem" }}>
              {[
                { label: "FBA Fees", value: s.fba_fees, color: "#e53e3e" },
                { label: "Ad Spend (incl. GST)", value: s.ad_spend, color: "#805ad5" },
                { label: "Refunds Given", value: s.refunds, color: "#dd6b20" },
                { label: "Promotions", value: s.promotions, color: "#d69e2e" },
                { label: "COGS", value: -(s.cogs || 0), color: "#2b6cb0" },
                { label: "Reimbursements", value: s.reimbursements, color: "#38a169" },
              ].map(item => (
                <div key={item.label} style={{ padding: "1rem", background: "#fafafa",
                  borderRadius: "8px", borderLeft: `3px solid ${item.color}` }}>
                  <div style={{ fontSize: "0.75rem", color: "#888", marginBottom: "4px" }}>{item.label}</div>
                  <div style={{ fontWeight: 700, color: item.color, fontSize: "1.1rem" }}>
                    {(item.value || 0) < 0 ? "-" : "+"}{fmt(item.value || 0)}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "1.5rem", padding: "1rem", background: "#f0f4ff",
              borderRadius: "8px", display: "flex", gap: "8px", flexWrap: "wrap",
              alignItems: "center", fontSize: "0.875rem" }}>
              <span style={{ color: "#4361ee", fontWeight: 600 }}>{fmt(s.revenue)}</span>
              <span style={{ color: "#888" }}>revenue −</span>
              <span style={{ color: "#e53e3e", fontWeight: 600 }}>{fmt(Math.abs(s.fba_fees || 0))}</span>
              <span style={{ color: "#888" }}>fees −</span>
              <span style={{ color: "#805ad5", fontWeight: 600 }}>{fmt(Math.abs(s.ad_spend || 0))}</span>
              <span style={{ color: "#888" }}>ads −</span>
              <span style={{ color: "#dd6b20", fontWeight: 600 }}>{fmt(Math.abs(s.refunds || 0))}</span>
              <span style={{ color: "#888" }}>refunds −</span>
              <span style={{ color: "#2b6cb0", fontWeight: 600 }}>{fmt(s.cogs || 0)}</span>
              <span style={{ color: "#888" }}>COGS +</span>
              <span style={{ color: "#38a169", fontWeight: 600 }}>{fmt(s.reimbursements || 0)}</span>
              <span style={{ color: "#888" }}>reimbursements =</span>
              <span style={{ fontWeight: 700, fontSize: "1rem",
                color: (s.profit || 0) >= 0 ? "#38a169" : "#e53e3e" }}>
                {fmt(s.profit || 0)} profit
              </span>
            </div>
          </div>

          {/* Product Performance */}
          <div className="chart-card">
            <div className="chart-title" style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Product Performance</span>
              {mode === "range" && (
                <span style={{ fontSize: "0.8rem", color: "#888", fontWeight: 400 }}>
                  Revenue only — fees allocated by MTD share
                </span>
              )}
              {mode === "mtd" && (
                <span style={{ fontSize: "0.8rem", color: "#888", fontWeight: 400 }}>
                  Ad spend allocated by revenue share
                </span>
              )}
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Units</th>
                    <th>Revenue</th>
                    {mode === "mtd" && <>
                      <th>Fees</th>
                      <th>Ad Spend</th>
                      <th>COGS</th>
                      <th>Profit</th>
                      <th>Margin</th>
                    </>}
                  </tr>
                </thead>
                <tbody>
                  {mode === "mtd" ? (
                    <>
                      {products?.map(p => (
                        <tr key={p.product_name}>
                          <td style={{ fontWeight: 600 }}>📦 {p.product_name}</td>
                          <td>{p.total_units}</td>
                          <td style={{ color: "#4361ee", fontWeight: 600 }}>
                            {fmt(p.total_revenue)}
                          </td>
                          <td style={{ color: "#e53e3e" }}>{fmt(p.total_fees)}</td>
                          <td style={{ color: "#805ad5" }}>{fmt(p.allocated_ad_spend || 0)}</td>
                          <td style={{ color: "#dd6b20" }}>{fmt(p.total_cogs)}</td>
                          <td style={{ fontWeight: 700,
                            color: p.total_profit >= 0 ? "#38a169" : "#e53e3e" }}>
                            {fmt(p.total_profit)}
                          </td>
                          <td>
                            <span className={`badge ${p.total_margin >= 30 ? "green" : p.total_margin >= 0 ? "orange" : "red"}`}>
                              {p.total_margin}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: "2px solid #e0e0e0", fontWeight: 700, background: "#fafafa" }}>
                        <td>TOTAL</td>
                        <td>{products?.reduce((s, p) => s + p.total_units, 0)}</td>
                        <td style={{ color: "#4361ee" }}>{fmt(products?.reduce((s, p) => s + p.total_revenue, 0))}</td>
                        <td style={{ color: "#e53e3e" }}>{fmt(products?.reduce((s, p) => s + p.total_fees, 0))}</td>
                        <td style={{ color: "#805ad5" }}>{fmt(products?.reduce((s, p) => s + (p.allocated_ad_spend || 0), 0))}</td>
                        <td style={{ color: "#dd6b20" }}>{fmt(products?.reduce((s, p) => s + p.total_cogs, 0))}</td>
                        <td style={{ color: "#38a169" }}>{fmt(products?.reduce((s, p) => s + p.total_profit, 0))}</td>
                        <td></td>
                      </tr>
                    </>
                  ) : (
                    <>
                      {rangeData?.products?.map(p => (
                        <tr key={p.product_name}>
                          <td style={{ fontWeight: 600 }}>📦 {p.product_name}</td>
                          <td>{p.units}</td>
                          <td style={{ color: "#4361ee", fontWeight: 600 }}>{fmt(p.revenue)}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: "2px solid #e0e0e0", fontWeight: 700, background: "#fafafa" }}>
                        <td>TOTAL</td>
                        <td>{rangeData?.units_sold}</td>
                        <td style={{ color: "#4361ee" }}>{fmt(rangeData?.revenue)}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
